import { Fragment } from 'react';

// Le texte « riche » de la documentation et du Tutoriel : DEUX marques seulement, **gras** et `code`, et jamais
// de HTML. Le contenu du Tutoriel est écrit par des admins, à l'écran (N32.6) : un texte rendu tel quel en HTML
// serait une porte ouverte, et deux marques suffisent à tout ce que la maquette voulait dire.
//
// Sorti de Documentation.tsx le 29-09 (N32.6) : le Tutoriel en a besoin aussi, et il est importé PAR
// Documentation.tsx — le garder là-bas aurait fait un import circulaire.

const CODE_CLASS = 'rounded bg-slate-100 px-1 py-0.5 text-[0.85em] font-mono text-slate-700';

/** Une ligne de texte : **gras** et `code`. */
export function Rich({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**') && p.length > 4) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (p.startsWith('`') && p.endsWith('`') && p.length > 2) return <code key={i} className={CODE_CLASS}>{p.slice(1, -1)}</code>;
        return <Fragment key={i}>{p}</Fragment>;
      })}
    </>
  );
}

type Block = { kind: 'p'; lines: string[] } | { kind: 'ul' | 'ol'; items: string[] };

/** Découpe un texte en blocs : une ligne vide sépare deux blocs ; un bloc dont toutes les lignes commencent
 *  par « - » (ou « • ») est une liste à puces, par « 1. » une liste numérotée ; sinon c'est un paragraphe, dont
 *  les retours à la ligne sont gardés. */
function toBlocks(text: string): Block[] {
  return text
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n/)
    .map((chunk) => chunk.split('\n').map((l) => l.trim()).filter(Boolean))
    .filter((lines) => lines.length > 0)
    .map((lines): Block => {
      if (lines.every((l) => /^[-•]\s+/.test(l))) return { kind: 'ul', items: lines.map((l) => l.replace(/^[-•]\s+/, '')) };
      if (lines.every((l) => /^\d+[.)]\s+/.test(l))) return { kind: 'ol', items: lines.map((l) => l.replace(/^\d+[.)]\s+/, '')) };
      return { kind: 'p', lines };
    });
}

/** Un texte de plusieurs paragraphes et listes, chaque ligne rendue par `Rich`. */
export function RichBlock({ text, className }: { text: string; className?: string }) {
  const blocks = toBlocks(text);
  return (
    <div className={className ?? 'space-y-2'}>
      {blocks.map((b, i) => {
        if (b.kind === 'p') {
          return (
            <p key={i}>
              {b.lines.map((l, j) => <Fragment key={j}>{j > 0 && <br />}<Rich text={l} /></Fragment>)}
            </p>
          );
        }
        const items = b.items.map((it, j) => <li key={j}><Rich text={it} /></li>);
        return b.kind === 'ul'
          ? <ul key={i} className="list-disc space-y-1 pl-5 marker:text-brand-rose">{items}</ul>
          : <ol key={i} className="list-decimal space-y-1 pl-5 marker:font-semibold marker:text-brand-rose">{items}</ol>;
      })}
    </div>
  );
}
